// src/components/ui-components.js
import React from 'react';

// ============================================================================
// ENHANCED BASE BUTTON COMPONENT
// ============================================================================

export const ActionButton = ({
  children,
  variant = 'secondary',
  size = 'md',
  onClick,
  disabled = false,
  type = 'button',
  className = '',
  loading = false,
  icon: IconComponent,
  style = {},
  shadow = true,
  rounded = 'md',
  gradient = false,
  ...props
}) => {
  const sizeClass = {
    sm: 'btn-sm px-3',
    md: 'px-4',
    lg: 'btn-lg px-5'
  }[size];

  const roundedClass = {
    none: '',
    sm: 'rounded',
    md: 'rounded-2',
    lg: 'rounded-3',
    pill: 'rounded-pill'
  }[rounded];

  const shadowClass = shadow ? 'shadow-sm' : '';
  
  // Handle gradient with inline styles instead of CSS classes
  const gradientStyles = gradient ? {
    background: `linear-gradient(135deg, var(--bs-${variant}), var(--bs-${variant}-dark))`,
    border: 'none',
    color: 'white'
  } : {};

  const baseClasses = `btn btn-${variant} ${sizeClass} ${roundedClass} ${shadowClass} transition-all`;
  const finalClasses = `${baseClasses} ${className}`.trim();

  return (
    <button
      type={type}
      className={finalClasses}
      onClick={onClick}
      disabled={disabled || loading}
      style={{
        transition: 'all 0.3s ease',
        border: '1px solid transparent',
        position: 'relative',
        overflow: 'hidden',
        ...gradientStyles,
        ...style
      }}
      {...props}
    >
      {loading && (
        <span 
          className="spinner-border spinner-border-sm me-2" 
          style={{ width: '1rem', height: '1rem' }}
        />
      )}
      {IconComponent && <IconComponent className={children ? "me-1" : ""} />}
      {children}
    </button>
  );
};

// ============================================================================
// CUSTOM BUTTON WRAPPER COMPONENT
// ============================================================================

export const CustomButtonWrapper = ({ Component, name, onClick, ...rest }) => {
  return (
    <Component onClick={onClick} {...rest}>
      {name}
    </Component>
  );
};

// ============================================================================
// TOGGLE SWITCH COMPONENT - VERIFIED ✅
// ============================================================================

export const ToggleSwitch = ({
  id,
  checked = false,
  onChange,
  disabled = false,
  size = 'md',
  className = '',
  ...props
}) => {
  const sizeStyles = {
    sm: { width: '40px', height: '20px' },
    md: { width: '48px', height: '24px' },
    lg: { width: '56px', height: '28px' }
  }[size];

  return (
    <label 
      htmlFor={id}
      className={`checktoggle ${checked ? 'checked' : ''} ${disabled ? 'disabled' : ''} ${className}`}
      style={{
        backgroundColor: checked ? '#28a745' : '#e0001a',
        borderRadius: '12px',
        cursor: disabled ? 'not-allowed' : 'pointer',
        display: 'block',
        fontSize: '0',
        height: sizeStyles.height,
        marginBottom: '0',
        position: 'relative',
        width: sizeStyles.width,
        opacity: disabled ? 0.6 : 1,
        transition: 'all 0.3s ease',
        border: '2px solid transparent'
      }}
    >
      <input
        type="checkbox"
        id={id}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        style={{ display: 'none' }}
        {...props}
      />
      <span 
        style={{
          position: 'absolute',
          top: '2px',
          left: checked ? `calc(100% - ${parseInt(sizeStyles.height) - 4}px)` : '2px',
          width: `${parseInt(sizeStyles.height) - 8}px`,
          height: `${parseInt(sizeStyles.height) - 8}px`,
          backgroundColor: 'white',
          borderRadius: '50%',
          transition: 'all 0.3s ease',
          boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
        }}
      />
    </label>
  );
};

// ============================================================================
// RATING COMPONENTS - VERIFIED ✅
// ============================================================================

export const StarRating = ({
  rating = 0,
  maxStars = 5,
  size = 'md',
  color = '#ffbc34',
  onRate,
  readonly = false,
  className = '',
  ...props
}) => {
  const sizeStyles = {
    sm: { fontSize: '14px', margin: '0 1px' },
    md: { fontSize: '16px', margin: '0 2px' },
    lg: { fontSize: '20px', margin: '0 3px' }
  }[size];

  return (
    <div className={`d-inline-flex align-items-center ${className}`} {...props}>
      {[...Array(maxStars)].map((_, index) => (
        <i
          key={index}
          className={`bi bi-star${index < rating ? '-fill' : ''} ${index < rating ? 'text-warning' : 'text-muted'}`}
          style={{
            fontSize: sizeStyles.fontSize,
            margin: sizeStyles.margin,
            color: index < rating ? color : '#6c757d',
            cursor: readonly ? 'default' : 'pointer',
            transition: 'color 0.2s ease'
          }}
          onClick={() => !readonly && onRate && onRate(index + 1)}
        />
      ))}
    </div>
  );
};

export const ThumbsRating = ({
  likes = 0,
  dislikes = 0,
  userVote = null,
  onLike,
  onDislike,
  size = 'md',
  className = '',
  ...props
}) => {
  const sizeStyles = {
    sm: { fontSize: '12px', padding: '0.25rem 0.5rem' },
    md: { fontSize: '14px', padding: '0.375rem 0.75rem' },
    lg: { fontSize: '16px', padding: '0.5rem 1rem' }
  }[size];

  return (
    <div className={`btn-group ${className}`} role="group" {...props}>
      <button
        type="button"
        className={`btn ${userVote === 'like' ? 'btn-success' : 'btn-outline-success'}`}
        onClick={onLike}
        aria-pressed={userVote === 'like'}
        aria-label="Like"
        style={{
          fontSize: sizeStyles.fontSize,
          padding: sizeStyles.padding
        }}
      >
        <i 
          className="bi bi-hand-thumbs-up me-1" 
          style={{ color: userVote === 'like' ? 'white' : '#28a745' }}
        />
        Like ({likes})
      </button>
      <button
        type="button"
        className={`btn ${userVote === 'dislike' ? 'btn-danger' : 'btn-outline-danger'}`}
        onClick={onDislike}
        aria-pressed={userVote === 'dislike'}
        aria-label="Dislike"
        style={{
          fontSize: sizeStyles.fontSize,
          padding: sizeStyles.padding
        }}
      >
        <i 
          className="bi bi-hand-thumbs-down me-1" 
          style={{ color: userVote === 'dislike' ? 'white' : '#dc3545' }}
        />
        Dislike ({dislikes})
      </button>
    </div>
  );
};

export const ViewCount = ({ count = 0, size = 'md', className = '', ...props }) => {
  const sizeStyles = {
    sm: { fontSize: '12px', iconSize: '12px' },
    md: { fontSize: '14px', iconSize: '14px' },
    lg: { fontSize: '16px', iconSize: '16px' }
  }[size];

  return (
    <span 
      className={`d-inline-flex align-items-center ${className}`}
      style={{ fontSize: sizeStyles.fontSize }}
      {...props}
    >
      <i 
        className="bi bi-eye me-1" 
        style={{ 
          color: '#5bc0de',
          fontSize: sizeStyles.iconSize
        }}
      />
      {count}
    </span>
  );
};

// ============================================================================
// ENHANCED STATUS LABEL COMPONENT - VERIFIED ✅
// ============================================================================

export const StatusLabel = ({
  children,
  variant = 'secondary',
  className = '',
  pill = true,
  shadow = true,
  icon = null,
  size = 'md',
  soft = false,
  style = {},
  removable = false,
  onRemove,
  ...props
}) => {
  const sizeStyles = {
    sm: { 
      fontSize: '0.65em',
      padding: '0.25em 0.65em',
      height: '20px',
      lineHeight: '1.2'
    },
    md: { 
      fontSize: '0.75em',
      padding: '0.35em 0.65em',
      height: '24px',
      lineHeight: '1.2'
    },
    lg: { 
      fontSize: '0.85em',
      padding: '0.45em 0.85em',
      height: '28px',
      lineHeight: '1.2'
    }
  }[size];

  const shadowClass = shadow ? 'shadow-sm' : '';
  const pillClass = pill ? 'rounded-pill' : 'rounded-2';
  const variantClass = soft ? `bg-${variant}-soft text-${variant}` : `bg-${variant}`;

  return (
    <span 
      className={`badge ${variantClass} ${pillClass} ${shadowClass} fw-medium ${className}`} 
      style={{
        transition: 'all 0.3s ease',
        border: '1px solid transparent',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: sizeStyles.fontSize,
        padding: sizeStyles.padding,
        height: sizeStyles.height,
        lineHeight: sizeStyles.lineHeight,
        minWidth: sizeStyles.height,
        ...style
      }}
      {...props}
    >
      {icon && <i className={`bi ${icon} me-1`} style={{ fontSize: '0.9em' }}></i>}
      {children}
      {removable && onRemove && (
        <button
          type="button"
          className="btn-close btn-close-white ms-1"
          style={{ 
            fontSize: '0.6em',
            width: '0.6em',
            height: '0.6em',
            backgroundSize: '0.6em',
            marginLeft: '4px'
          }}
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          aria-label="Remove"
        />
      )}
    </span>
  );
};

// ============================================================================
// CORE ACTION BUTTONS - UPDATED WITH BOOTSTRAP CLASSES
// ============================================================================

export const ViewButton = ({ onClick, disabled, size, children, textOnly = false, variant = 'info', ...props }) => (
  <ActionButton
    variant={variant}
    size={size}
    onClick={onClick}
    disabled={disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    {...(!textOnly && { icon: () => <i className="bi bi-eye"></i> })}
    {...props}
  >
    
    {children || 'View'}
  </ActionButton>
);

export const EditButton = ({ onClick, disabled, size, children, ...props }) => (
  <ActionButton
    size={size}
    onClick={onClick}
    disabled={disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-pencil"></i>}
    style={{ backgroundColor: '#0d622b', color: 'white' }}
    {...props}
  >
    {children || 'Edit'}
  </ActionButton>
);

export const UpdateButton = ({ onClick, disabled, size, children, ...props }) => (
  <ActionButton
    variant="info"
    size={size}
    onClick={onClick}
    disabled={disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-arrow-clockwise"></i>}
    {...props}
  >
    {children || 'Update'}
  </ActionButton>
);

export const DeleteButton = ({ onClick, disabled, size, children, confirm = false, ...props }) => (
  <ActionButton
    variant="danger"
    size={size}
    onClick={onClick}
    disabled={disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-trash"></i>}
    {...props}
  >
    {children || (confirm ? 'Confirm Delete' : 'Delete')}
  </ActionButton>
);

export const AddButton = ({ onClick, disabled, size, children, ...props }) => (
  <ActionButton
    variant="info"
    size={size}
    onClick={onClick}
    disabled={disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-plus-circle"></i>}
    {...props}
  >
    {children || 'Add'}
  </ActionButton>
);

export const SaveButton = ({ onClick, disabled, size, children, ...props }) => (
  <ActionButton
    variant="info"
    size={size}
    onClick={onClick}
    disabled={disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-check-lg"></i>}
    {...props}
  >
    {children || 'Save'}
  </ActionButton>
);

export const CancelButton = ({ onClick, disabled, size, children, ...props }) => (
  <ActionButton
    variant="warning"
    size={size}
    onClick={onClick}
    disabled={disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-x-circle"></i>}
    {...props}
  >
    {children || 'Cancel'}
  </ActionButton>
);

export const ConfirmButton = ({ onClick, disabled, size, children, ...props }) => (
  <ActionButton
    variant="info"
    size={size}
    onClick={onClick}
    disabled={disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-check-circle"></i>}
    {...props}
  >
    {children || 'Confirm'}
  </ActionButton>
);

export const ClearButton = ({ onClick, disabled, size, children, ...props }) => (
  <ActionButton
    variant="warning"
    size={size}
    onClick={onClick}
    disabled={disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-eraser"></i>}
    {...props}
  >
    {children || 'Clear'}
  </ActionButton>
);

export const LinkButton = ({ onClick, disabled, size, children, variant = 'link', ...props }) => (
  <ActionButton
    variant={variant}
    size={size}
    onClick={onClick}
    disabled={disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-link"></i>}
    {...props}
  >
    {children || 'Link'}
  </ActionButton>
);

export const TransferButton = ({ onClick, disabled, size, children, ...props }) => (
  <ActionButton
    variant="info"
    size={size}
    onClick={onClick}
    disabled={disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-arrow-left-right"></i>}
    {...props}
  >
    {children || 'Transfer'}
  </ActionButton>
);

export const SeeMoreButton = ({ onClick, disabled, size, children, variant = 'link', ...props }) => (
  <ActionButton
    variant={variant}
    size={size}
    onClick={onClick}
    disabled={disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-three-dots"></i>}
    {...props}
  >
    {children || 'See More'}
  </ActionButton>
);

// ============================================================================
// PREMIUM BUTTON VARIANTS - UPDATED WITH BOOTSTRAP CLASSES
// ============================================================================

// View Button Variations
export const ViewButtonRounded = (props) => <ViewButton {...props} textOnly={true} className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"/>;



// );
export const ViewButtonIconOnly = (props) => (
  <ActionButton
    variant="light"
    size={props.size}
    onClick={props.onClick}
    disabled={props.disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-eye"></i>}
    {...props}
  >
    {""}
  </ActionButton>
);

// Edit Button Variations


export const EditButtonRounded = (props) => <EditButton {...props} className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2" />;



export const EditButtonIconOnly = (props) => (
  <ActionButton
    size={props.size}
    onClick={props.onClick}
    disabled={props.disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-pencil"></i>}
    style={{ backgroundColor: '#0d622b', color: 'white' }}
    {...props}
  >
    {""}
  </ActionButton>
);



// Delete Button Variations
export const DeleteButtonRounded = (props) => <DeleteButton {...props} className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2" />;

export const DeleteButtonIconOnly = (props) => (
  <ActionButton
    variant="danger"
    size={props.size}
    onClick={props.onClick}
    disabled={props.disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-trash"></i>}
    {...props}
  >
    {""}
  </ActionButton>
);

export const DeleteButtonDangerSoft = (props) => (
  <DeleteButton {...props} className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2" style={{ 
    backgroundColor: 'rgba(220, 53, 69, 0.1)',
    color: '#dc3545',
    borderColor: 'rgba(220, 53, 69, 0.2)'
  }} />
);

// Add Button Variations
export const AddButtonRounded = (props) => <AddButton {...props} className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2" />;

export const AddButtonIconOnly = (props) => (
  <ActionButton
    variant="info"
    size={props.size}
    onClick={props.onClick}
    disabled={props.disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-plus-circle"></i>}
    {...props}
  >
    {""}
  </ActionButton>
);


// Save Button Variations
export const SaveButtonRounded = (props) => <SaveButton {...props} className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2" />;

export const SaveButtonIconOnly = (props) => (
  <ActionButton
    variant="info"
    size={props.size}
    onClick={props.onClick}
    disabled={props.disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-check-lg"></i>}
    {...props}
  >
    {""}
  </ActionButton>
);


// Cancel Button Variations
export const CancelButtonRounded = (props) => <CancelButton {...props} className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2" />;

export const CancelButtonIconOnly = (props) => (
  <ActionButton
    variant="warning"
    size={props.size}
    onClick={props.onClick}
    disabled={props.disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-x-circle"></i>}
    {...props}
  >
    {""}
  </ActionButton>
);


// Clear Button Variations
export const ClearButtonRounded = (props) => <ClearButton {...props} className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2" />;

export const ClearButtonIconOnly = (props) => (
  <ActionButton
    variant="warning"
    size={props.size}
    onClick={props.onClick}
    disabled={props.disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-eraser"></i>}
    {...props}
  >
    {""}
  </ActionButton>
);



// ============================================================================
// ADDITIONAL PREMIUM BUTTONS - UPDATED WITH BOOTSTRAP CLASSES
// ============================================================================

export const DownloadButton = ({ variant = 'default', ...props }) => {
  return (
    <ActionButton 
      className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
      style={{ backgroundColor: '#0d622b', color: 'white' }}
      icon={() => <i className="bi bi-download"></i>}
      {...props}
    >
      Download
    </ActionButton>
  );
};

export const UploadButton = ({ variant = 'default', ...props }) => {
  return (
    <ActionButton
      variant="info"
      className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
      icon={() => <i className="bi bi-upload"></i>}
      {...props}
    >
      Upload
    </ActionButton>
  );
};

export const FilterButton = ({ variant = 'default', ...props }) => {
  return (
    <ActionButton
      variant="secondary"
      className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
      icon={() => <i className="bi bi-funnel"></i>}
      {...props}
    >
      Filter Results
    </ActionButton>
  );
};

// ============================================================================
// OUTLINE BUTTONS - UPDATED WITH BOOTSTRAP CLASSES
// ============================================================================

export const OutlineViewButton = ({ onClick, disabled, size, children, ...props }) => (
  <ActionButton
    variant="outline-info"
    size={size}
    onClick={onClick}
    disabled={disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-eye"></i>}
    {...props}
  >
    {children || 'View'}
  </ActionButton>
);

export const OutlineEditButton = ({ onClick, disabled, size, children, ...props }) => (
  <ActionButton
    variant="outline-primary"
    size={size}
    onClick={onClick}
    disabled={disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-pencil"></i>}
    {...props}
  >
    {children || 'Edit'}
  </ActionButton>
);

export const OutlineDeleteButton = ({ onClick, disabled, size, children, ...props }) => (
  <ActionButton
    variant="outline-danger"
    size={size}
    onClick={onClick}
    disabled={disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-trash"></i>}
    {...props}
  >
    {children || 'Delete'}
  </ActionButton>
);

// ============================================================================
// ENHANCED TOGGLE BUTTONS - UPDATED WITH BOOTSTRAP CLASSES
// ============================================================================

export const PublishToggle = ({ isPublished, onToggle, disabled, size = 'md', variant = 'default' }) => {
  const currentVariant = isPublished ? 'btn-info' : 'btn-secondary';
  const currentIcon = isPublished ? 'bi-toggle-on' : 'bi-toggle-off';

  return (
    <ActionButton
      variant={isPublished ? 'info' : 'secondary'}
      size={size}
      onClick={(e) => {
        e.preventDefault();
        onToggle(!isPublished);
      }}
      disabled={disabled}
      className={`btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2 fw-medium ${currentVariant}`}
    >
      <i className={`bi ${currentIcon} me-2`}></i>
      {isPublished ? 'Published' : 'Unpublished'}
    </ActionButton>
  );
};

export const StatusToggle = ({ isActive, onToggle, disabled, size = 'md', activeText = 'Active', inactiveText = 'Inactive', variant = 'default' }) => {
  const currentVariant = isActive ? 'btn-info' : 'btn-secondary';
  const currentIcon = isActive ? 'bi-toggle-on' : 'bi-toggle-off';

  return (
    <ActionButton
      variant={isActive ? 'info' : 'secondary'}
      size={size}
      onClick={(e) => {
        e.preventDefault();
        onToggle(!isActive);
      }}
      disabled={disabled}
      className={`btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2 fw-medium ${currentVariant}`}
    >
      <i className={`bi ${currentIcon} me-2`}></i>
      {isActive ? activeText : inactiveText}
    </ActionButton>
  );
};

// ============================================================================
// PREMIUM STATUS LABEL COMPONENTS - VERIFIED ✅
// ============================================================================

export const SpecialtyLabel = ({ children, variant = 'default', size = 'md', ...props }) => {
  const variants = {
    default: { bg: 'info', icon: 'bi-award' },
    premium: { bg: 'warning', icon: 'bi-star-fill' },
    expert: { bg: 'danger', icon: 'bi-trophy' },
    basic: { bg: 'secondary', icon: 'bi-award' }
  }[variant];

  return (
    <StatusLabel variant={variants.bg} icon={variants.icon} size={size} {...props}>
      {children || 'Speciality'}
    </StatusLabel>
  );
};

export const NotificationLabel = ({ count, variant = 'default', size = 'md', ...props }) => {
  const variants = {
    default: { bg: 'danger', icon: 'bi-bell' },
    info: { bg: 'info', icon: 'bi-bell' },
    warning: { bg: 'warning', icon: 'bi-bell' },
    success: { bg: 'success', icon: 'bi-bell' }
  }[variant];

  return (
    <StatusLabel variant={variants.bg} icon={variants.icon} size={size} {...props}>
      Notification {count !== undefined && `(${count})`}
    </StatusLabel>
  );
};

export const PendingLabel = ({ variant = 'default', size = 'md', ...props }) => {
  const variants = {
    default: { bg: 'warning', icon: 'bi-clock' },
    light: { bg: 'warning', icon: 'bi-clock', className: 'bg-warning-soft text-warning' },
    outline: { bg: 'outline-warning', icon: 'bi-clock' }
  }[variant];

  return (
    <StatusLabel variant={variants.bg} icon={variants.icon} className={variants.className} size={size} {...props}>
      Pending
    </StatusLabel>
  );
};

export const OnlineLabel = ({ variant = 'default', size = 'md', ...props }) => {
  const variants = {
    default: { bg: 'success', icon: 'bi-circle-fill' },
    pulse: { bg: 'success', icon: 'bi-circle-fill', className: 'pulse-animation' },
    light: { bg: 'success', icon: 'bi-circle-fill', className: 'bg-success-soft text-success' }
  }[variant];

  return (
    <StatusLabel variant={variants.bg} icon={variants.icon} className={variants.className} size={size} {...props}>
      Online
    </StatusLabel>
  );
};

export const ProcessingLabel = ({ variant = 'default', size = 'md', ...props }) => {
  const variants = {
    default: { bg: 'info', icon: 'bi-arrow-repeat' },
    spin: { bg: 'info', icon: 'bi-arrow-repeat', className: 'spin-animation' },
    light: { bg: 'info', icon: 'bi-arrow-repeat', className: 'bg-info-soft text-info' }
  }[variant];

  return (
    <StatusLabel variant={variants.bg} icon={variants.icon} className={variants.className} size={size} {...props}>
      Processing
    </StatusLabel>
  );
};

export const PaidLabel = ({ variant = 'default', size = 'md', ...props }) => {
  const variants = {
    default: { bg: 'success', icon: 'bi-currency-dollar' },
    light: { bg: 'success', icon: 'bi-currency-dollar', className: 'bg-success-soft text-success' },
    premium: { bg: 'success', icon: 'bi-gem' }
  }[variant];

  return (
    <StatusLabel variant={variants.bg} icon={variants.icon} className={variants.className} size={size} {...props}>
      Paid
    </StatusLabel>
  );
};

export const CancelLabel = ({ size = 'md', ...props }) => (
  <StatusLabel variant="danger" className="text-uppercase" size={size} {...props}>
    <i className="bi bi-x-circle me-1"></i>
    Cancel
  </StatusLabel>
);

export const PublishLabel = ({ size = 'md', ...props }) => (
  <StatusLabel variant="primary" style={{ backgroundColor: '#0084d9', color: 'white' }} className="text-uppercase" size={size} {...props}>
    <i className="bi bi-check-circle me-1"></i>
    Publish
  </StatusLabel>
);

export const UnpublishLabel = ({ size = 'md', ...props }) => (
  <StatusLabel variant="danger" className="text-uppercase" size={size} {...props}>
    <i className="bi bi-circle me-1"></i>
    Unpublish
  </StatusLabel>
);

export const FailedLabel = ({ size = 'md', ...props }) => (
  <StatusLabel variant="danger" className="text-uppercase" size={size} {...props}>
    <i className="bi bi-exclamation-triangle me-1"></i>
    Failed
  </StatusLabel>
);

// Additional Status Labels - VERIFIED ✅
export const CreatedLabel = ({ size = 'md', ...props }) => (
  <StatusLabel variant="secondary" size={size} {...props}>
    Created
  </StatusLabel>
);

export const DraftLabel = ({ size = 'md', ...props }) => (
  <StatusLabel variant="secondary" size={size} {...props}>
    Draft
  </StatusLabel>
);

export const EditedLabel = ({ size = 'md', ...props }) => (
  <StatusLabel variant="secondary" size={size} {...props}>
    Edited
  </StatusLabel>
);

export const ActiveLabel = ({ size = 'md', ...props }) => (
  <StatusLabel variant="success" pill soft size={size} {...props}>
    <i className="bi bi-check-circle me-1"></i>
    Active
  </StatusLabel>
);

export const SubscriptionLabel = ({ size = 'md', ...props }) => (
  <StatusLabel variant="success" pill soft size={size} {...props}>
    <i className="bi bi-check-circle me-1"></i>
    Subscription
  </StatusLabel>
);

export const CompleteLabel = ({ size = 'md', ...props }) => (
  <StatusLabel variant="success" pill className="bg-success-soft text-success" size={size} {...props}>
    <i className="bi bi-check-circle me-1"></i>
    Complete
  </StatusLabel>
);

export const PendingLightLabel = ({ size = 'md', ...props }) => (
  <StatusLabel variant="warning" pill soft size={size} {...props}>
    <i className="bi bi-clock me-1"></i>
    Pending
  </StatusLabel>
);

export const OnlineLightLabel = ({ size = 'md', ...props }) => (
  <StatusLabel variant="info" pill soft size={size} {...props}>
    <i className="bi bi-circle-fill me-1"></i>
    Online
  </StatusLabel>
);

export const ProcessingLightLabel = ({ size = 'md', ...props }) => (
  <StatusLabel variant="primary" pill soft size={size} {...props}>
    <i className="bi bi-gear me-1"></i>
    Processing
  </StatusLabel>
);

export const PaidLightLabel = ({ size = 'md', ...props }) => (
  <StatusLabel variant="info" pill soft size={size} {...props}>
    <i className="bi bi-currency-dollar me-1"></i>
    Paid
  </StatusLabel>
);

export const CancelLightLabel = ({ size = 'md', ...props }) => (
  <StatusLabel variant="danger" pill soft size={size} {...props}>
    <i className="bi bi-x-circle me-1"></i>
    Cancelled
  </StatusLabel>
);

export const PublishLightLabel = ({ size = 'md', ...props }) => (
  <StatusLabel variant="primary" pill soft style={{ backgroundColor: '#e0f2f7', color: '#0084d9' }} size={size} {...props}>
    <i className="bi bi-check-circle me-1"></i>
    Publish
  </StatusLabel>
);

export const UnpublishLightLabel = ({ size = 'md', ...props }) => (
  <StatusLabel variant="danger" pill soft size={size} {...props}>
    <i className="bi bi-circle me-1"></i>
    Unpublish
  </StatusLabel>
);

export const FailedLightLabel = ({ size = 'md', ...props }) => (
  <StatusLabel variant="danger" pill soft size={size} {...props}>
    <i className="bi bi-exclamation-triangle me-1"></i>
    Failed
  </StatusLabel>
);

export const ConfirmLabel = ({ size = 'md', ...props }) => (
  <StatusLabel variant="success" pill soft style={{ backgroundColor: '#e6ffe6', color: '#0d622b' }} size={size} {...props}>
    <i className="bi bi-check-circle me-1"></i>
    Confirm
  </StatusLabel>
);

export const NotBilledLabel = ({ size = 'md', ...props }) => (
  <StatusLabel variant="light" pill className="text-dark border" size={size} {...props}>
    <i className="bi bi-x-circle me-1"></i>
    Not Billed
  </StatusLabel>
);

export const CountBadge = ({ count, size = 'md', ...props }) => (
  <StatusLabel variant="light" pill className="text-muted border" size={size} {...props}>
    {count}
  </StatusLabel>
);

export const RemovableSpecialtyLabel = ({ children, onRemove, size = 'md', ...props }) => (
  <StatusLabel 
    variant="primary" 
    className="d-flex align-items-center" 
    style={{ 
      backgroundColor: '#0084d9', 
      color: 'white',
      padding: '8px 12px'
    }} 
    size={size}
    removable
    onRemove={onRemove}
    {...props}
  >
    {children}
  </StatusLabel>
);

// ============================================================================
// NOTIFICATION BADGE COMPONENT - VERIFIED ✅
// ============================================================================

export const NotificationBadge = ({ count, className = '', size = 'md', ...props }) => {
  const sizeStyles = {
    sm: { fontSize: '10px', padding: '2px 6px', minWidth: '16px' },
    md: { fontSize: '12px', padding: '3px 8px', minWidth: '20px' },
    lg: { fontSize: '14px', padding: '4px 10px', minWidth: '24px' }
  }[size];

  return (
    <small 
      className={`unread-msg badge bg-danger rounded-pill ${className}`}
      style={{
        fontSize: sizeStyles.fontSize,
        padding: sizeStyles.padding,
        minWidth: sizeStyles.minWidth,
        height: sizeStyles.minWidth,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1
      }}
      {...props}
    >
      {count}
    </small>
  );
};

// ============================================================================
// BUTTON GROUP COMPONENTS - VERIFIED ✅
// ============================================================================

export const ButtonGroup = ({ children, className = '', vertical = false, size = 'md' }) => {
  const sizeClass = {
    sm: 'btn-group-sm',
    md: '',
    lg: 'btn-group-lg'
  }[size];

  const directionClass = vertical ? 'btn-group-vertical' : 'btn-group';

  return (
    <div className={`${directionClass} ${sizeClass} ${className} shadow-sm`} role="group">
      {children}
    </div>
  );
};

export const ActionButtonGroup = ({ onView, onEdit, onDelete, size = 'sm', variant = 'default' }) => (
  <ButtonGroup size={size}>
    <ViewButton size={size} variant={variant} onClick={onView} />
    <EditButton size={size} variant={variant} onClick={onEdit} />
    <DeleteButton size={size} variant={variant} onClick={onDelete} />
  </ButtonGroup>
);

// ============================================================================
// SUBMIT BUTTONS - UPDATED WITH BOOTSTRAP CLASSES
// ============================================================================

export const SubmitButton = ({ children, disabled, size, className = '', ...props }) => (
  <ActionButton
    type="submit"
    variant="info"
    size={size}
    disabled={disabled}
    className={`btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2 submit-btn ${className}`}
    {...props}
  >
    {children || 'Submit'}
  </ActionButton>
);

export const SaveChangesButton = ({ children, disabled, size, ...props }) => (
  <SubmitButton disabled={disabled} size={size} className="px-4" {...props}>
    {children || 'Save Changes'}
  </SubmitButton>
);

export const UpdateProfileButton = ({ children, disabled, size = 'sm', ...props }) => (
  <SubmitButton disabled={disabled} size={size} {...props}>
    {children || 'Update Profile'}
  </SubmitButton>
);

// ============================================================================
// SPECIAL BUTTONS - UPDATED WITH BOOTSTRAP CLASSES
// ============================================================================

export const BookNowButton = ({ onClick, disabled, size, children, ...props }) => (
  <ActionButton
    variant="info"
    size={size}
    onClick={onClick}
    disabled={disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-calendar-check"></i>}
    {...props}
  >
    {children || 'Book Now'}
  </ActionButton>
);

export const SetHolidayButton = ({ onClick, disabled, size = 'sm', children, ...props }) => (
  <ActionButton
    variant="outline-danger"
    size={size}
    onClick={onClick}
    disabled={disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-calendar-x"></i>}
    {...props}
  >
    {children || 'Set as Holiday'}
  </ActionButton>
);

export const ViewProfileButton = ({ onClick, disabled, size, children, ...props }) => (
  <ActionButton
    variant="outline-info"
    size={size}
    onClick={onClick}
    disabled={disabled}
    className="btn btn-sm rounded-pill pl-3 pr-3 me-2 mb-2"
    icon={() => <i className="bi bi-person"></i>}
    {...props}
  >
    {children || 'View Profile'}
  </ActionButton>
);

// ============================================================================
// CREATE UI OBJECT FOR DEFAULT EXPORT
// ============================================================================

const UI = {
  // Base Component
  ActionButton,
  
  // Custom Button Wrapper
  CustomButtonWrapper,
  
  // Toggle and Rating Components
  ToggleSwitch,
  StarRating,
  ThumbsRating,
  ViewCount,
  
  // Core Buttons
  ViewButton, 
  EditButton, 
  UpdateButton, 
  DeleteButton, 
  AddButton, 
  SaveButton, 
  CancelButton, 
  ConfirmButton, 
  ClearButton, 
  LinkButton, 
  TransferButton, 
  SeeMoreButton,
  
  // Premium Button Variations
  ViewButtonRounded, 
  ViewButtonIconOnly,
  
  EditButtonRounded, 
 
  EditButtonIconOnly, 

  
  DeleteButtonRounded, 
  DeleteButtonIconOnly, 
  DeleteButtonDangerSoft,
  
  AddButtonRounded, 
  AddButtonIconOnly, 
 
  
  SaveButtonRounded, 
  SaveButtonIconOnly, 

  
  CancelButtonRounded, 
  CancelButtonIconOnly, 

  
  ClearButtonRounded, 
  ClearButtonIconOnly, 

  
  // Additional Buttons
  DownloadButton, 
  UploadButton, 
  FilterButton,
  
  // Outline Buttons
  OutlineViewButton, 
  OutlineEditButton, 
  OutlineDeleteButton,
  
  // Submit Buttons
  SubmitButton, 
  SaveChangesButton, 
  UpdateProfileButton,
  
  // Special Buttons
  BookNowButton, 
  SetHolidayButton, 
  ViewProfileButton,
  
  // Toggle Buttons
  PublishToggle, 
  StatusToggle,
  
  // Status Labels
  StatusLabel, 
  SpecialtyLabel, 
  NotificationLabel, 
  PendingLabel, 
  OnlineLabel, 
  ProcessingLabel, 
  PaidLabel, 
  CancelLabel, 
  PublishLabel, 
  UnpublishLabel, 
  FailedLabel,
  
  // Additional Labels
  CreatedLabel, 
  DraftLabel, 
  EditedLabel, 
  ActiveLabel, 
  SubscriptionLabel, 
  CompleteLabel, 
  PendingLightLabel, 
  OnlineLightLabel, 
  ProcessingLightLabel, 
  PaidLightLabel, 
  CancelLightLabel, 
  PublishLightLabel, 
  UnpublishLightLabel, 
  FailedLightLabel, 
  ConfirmLabel, 
  NotBilledLabel, 
  CountBadge, 
  RemovableSpecialtyLabel,
  
  // Notification
  NotificationBadge,
  
  // Groups
  ButtonGroup, 
  ActionButtonGroup
};

export default UI;